/* eslint-disable react/no-unstable-nested-components */
import { ExclamationCircleOutlined, FilterTwoTone, FormOutlined, PlusCircleFilled } from "@ant-design/icons";
import { VipGiftList } from "@api/vipPromotionApi";
import PageTitle from "@component/PageTitle";
import BatchActivePopup from "@page/Sale/BatchActivePopup";
import {
  fetchBatchDeleteVipGift,
  fetchBatchUpdateVipGiftStatus,
  fetchVipGiftList,
  setVipBulletinListFilter,
  setVipGiftListFilter,
  VipRuleState,
} from "@redux/vipPromotionSlice";
import { Button, Checkbox, Modal, Row, Select, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import EditVipGiftPopup from "./EditVipGiftPopup";
import Filter from "./Filter";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 21px;
`;
const TopButtonContainer = styled.div``;
const TablePageInfo = styled.div``;
const TopButton = styled(Button)`
  margin-right: 10px;
`;
const PageSelect = styled(Select)`
  margin: 0 10px;
`;
const TableContainer = styled.div`
  position: relative;
  padding: 28px 21px 31px 10px;
  border: 1px solid #f0f0f0;
`;
const DeleteButton = styled(Button)`
  &&& {
    position: absolute;
    left: 16px;
    bottom: 47px;
  }
`;
const EditTextContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;
const EditText = styled.div`
  font-size: 13px;
  color: #1890ff;
  cursor: pointer;
  margin: 0;
  margin-right: 22px;
`;
const DeleteText = styled.p`
  font-size: 13px;
  color: #1890ff;
  cursor: pointer;
  margin: 0;
  margin-right: 22px;
`;
const EditStatusIcon = styled(FormOutlined)<{ disabled: boolean }>`
  margin-left: 5px;
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

const VipGift: FC = () => {
  const dispatch = useDispatch();
  const { isFetching, vipGiftListResult, vipGiftListFilter } = useSelector(VipRuleState);

  const [checkGiftIds, setCheckGiftIds] = useState<Set<number>>(new Set());
  const [showBatchActivePopup, setShowBatchActivePopup] = useState<boolean>(false);
  const [showAddGiftPopup, setShowAddGiftPopup] = useState<boolean>(false);
  const [editVipGift, setEditVipGift] = useState<VipGiftList | undefined>();
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const onCheckListItem = (id: number) => {
    if (checkGiftIds.has(id)) {
      const newSet = new Set<number>();
      newSet.delete(id);
      setCheckGiftIds(newSet);
    } else {
      setCheckGiftIds(new Set(checkGiftIds.add(id)));
    }
  };

  const onDeleteSingleData = (id: number) => {
    Modal.confirm({
      title: "你確定要刪除這筆資料?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "是",
      cancelText: "否",
      onOk: () => {
        dispatch(fetchBatchDeleteVipGift({ ids: [id] }));
      },
    });
  };

  const onDeleteMultiData = () => {
    Modal.confirm({
      title: "你確定要刪除這些資料?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "是",
      cancelText: "否",
      onOk: () => {
        dispatch(fetchBatchDeleteVipGift({ ids: Array.from(checkGiftIds) }));
      },
    });
  };

  const fetchPageChange = (value: number) => {
    window.scrollTo(0, 0);
    dispatch(setVipGiftListFilter({ ...vipGiftListFilter, offset: (value - 1) * vipGiftListFilter.limit }));
  };

  const onHandleCheckAllItem = () => {
    if (vipGiftListResult.results.length === checkGiftIds.size) {
      setCheckGiftIds(new Set());
    } else {
      setCheckGiftIds(new Set(vipGiftListResult.results.map((item) => item.id)));
    }
  };

  const fetchChangeGiftStatus = (isActive: boolean) => {
    const giftRuleData = Array.from(checkGiftIds).map((id) => ({ id, isActive }));
    dispatch(fetchBatchUpdateVipGiftStatus({ giftRuleData }));
    setShowBatchActivePopup(false);
  };

  useEffect(() => {
    dispatch(fetchVipGiftList(vipGiftListFilter));
  }, [dispatch, vipGiftListFilter]);

  useEffect(() => {
    dispatch(setVipBulletinListFilter({ limit: 20, offset: 0 }));
  }, [dispatch]);

  const tableColumns: ColumnsType<VipGiftList> = [
    {
      key: "checkbox",
      dataIndex: "checkbox",
      title: () => (
        <Checkbox checked={vipGiftListResult.results.length === checkGiftIds.size} onChange={onHandleCheckAllItem} />
      ),
      render: (_, values) => (
        <Checkbox checked={checkGiftIds.has(values.id)} onChange={() => onCheckListItem(values.id)} />
      ),
    },
    {
      title: "方案名稱",
      key: "name",
      dataIndex: "name",
      render: (_, values) => <div>{values.salesPlan.name}</div>,
    },
    {
      title: (
        <Row align="middle">
          <div>狀態</div>
          <EditStatusIcon onClick={() => setShowBatchActivePopup(true)} disabled={checkGiftIds.size === 0} />
        </Row>
      ),
      key: "isActive",
      dataIndex: "isActive",
      render: (_, values) => (values.salesPlan.isActive ? "上架" : "下架"),
    },
    {
      title: "使用時間",
      key: "timePeriod",
      dataIndex: "timePeriod",
      render: (_, values) => (
        <div>
          {values.useStartAt || values.useEndAt
            ? `${moment(values.useStartAt).format("YYYY-MM-DD HH:mm:ss")} - ${moment(values.useEndAt).format(
                "YYYY-MM-DD HH:mm:ss",
              )}`
            : "不限"}
        </div>
      ),
    },
    {
      title: "",
      width: 75,
      fixed: "right",
      render: (_, values) => {
        return (
          <EditTextContainer>
            <EditText
              onClick={() => {
                setEditVipGift(values);
                setShowAddGiftPopup(true);
              }}
            >
              編輯
            </EditText>
            <DeleteText onClick={() => onDeleteSingleData(values.id)}>刪除</DeleteText>
          </EditTextContainer>
        );
      },
    },
  ];

  return (
    <Wrapper>
      <PageTitle title="VIP 贈品" />
      <Header>
        <TopButtonContainer>
          <TopButton type="primary" icon={<PlusCircleFilled />} onClick={() => setShowAddGiftPopup(true)}>
            新增贈品
          </TopButton>
          <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
            篩選
          </Button>
        </TopButtonContainer>
        <TablePageInfo>
          總共 {vipGiftListResult.count} 筆, 每頁顯示
          <PageSelect
            value={vipGiftListFilter.limit}
            onChange={(value) => dispatch(setVipGiftListFilter({ ...vipGiftListFilter, limit: value as number }))}
          >
            {[20, 50, 100].map((option) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </PageSelect>
          筆
        </TablePageInfo>
      </Header>
      {showFilter && <Filter />}
      <TableContainer>
        <Table
          loading={isFetching}
          dataSource={vipGiftListResult.results}
          columns={tableColumns}
          pagination={{
            pageSize: vipGiftListFilter.limit,
            current: vipGiftListFilter.offset / vipGiftListFilter.limit + 1,
            showSizeChanger: false,
            total: vipGiftListResult.count,
            onChange: fetchPageChange,
          }}
        />
        <DeleteButton onClick={onDeleteMultiData} disabled={checkGiftIds.size === 0}>
          刪除
        </DeleteButton>
      </TableContainer>
      <BatchActivePopup
        visible={showBatchActivePopup}
        onClose={() => setShowBatchActivePopup(false)}
        onSubmit={fetchChangeGiftStatus}
        defaultValue={-1}
      />
      {showAddGiftPopup && (
        <EditVipGiftPopup
          vipGift={editVipGift}
          onClose={() => {
            setEditVipGift(undefined);
            setShowAddGiftPopup(false);
          }}
        />
      )}
    </Wrapper>
  );
};

export default VipGift;
